// PortraitSessionsGallery.js - Portrait Sessions Portfolio Gallery component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a portrait sessions portfolio gallery with image grid and professional styling
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The gallery elements
 */
export const createPortraitSessionsGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'portrait-gallery-section';
  const galleryContainerClass = 'portrait-gallery-container';
  const galleryHeaderClass = 'portrait-gallery-header';
  const galleryTitleClass = 'portrait-gallery-title';
  const gallerySubtitleClass = 'portrait-gallery-subtitle';
  const galleryGridClass = 'portrait-gallery-grid';
  const galleryImageItemClass = 'portrait-gallery-image-item';
  const galleryImageClass = 'portrait-gallery-image';
  const galleryImageOverlayClass = 'portrait-gallery-image-overlay';
  const galleryImageInfoClass = 'portrait-gallery-image-info';
  const galleryImageTitleClass = 'portrait-gallery-image-title';
  const galleryImageTypeClass = 'portrait-gallery-image-type';
  const galleryPatternOverlayClass = 'portrait-gallery-pattern-overlay';

  // Desktop Styles - Portrait theme with professional design
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8f9fa',
    background: `
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.04) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.03) 0%, transparent 50%),
      linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 25%, #e8eaed 50%, #f1f3f4 75%, #f8f9fa 100%)
    `,
    color: '#1f2937',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.02) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '70px 70px, 110px 110px',
    backgroundPosition: '0 0, 55px 55px',
    zIndex: '0',
    opacity: '0.5'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const galleryTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#1f2937',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(31, 41, 55, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '40px'
  };

  const galleryImageItemStyles = {
    position: 'relative',
    height: '450px',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid rgba(99, 102, 241, 0.1)',
    boxShadow: '0 4px 25px rgba(0, 0, 0, 0.06)'
  };

  const galleryImageItemHoverStyles = {
    transform: 'translateY(-10px) scale(1.02)',
    border: '1px solid rgba(99, 102, 241, 0.25)',
    boxShadow: `
      0 25px 50px rgba(0, 0, 0, 0.12),
      0 0 30px rgba(99, 102, 241, 0.15)
    `
  };

  const galleryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'brightness(1) contrast(1.02)'
  };

  const galleryImageHoverStyles = {
    filter: 'brightness(0.85) contrast(1.08)',
    transform: 'scale(1.05)'
  };

  const galleryImageOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(99, 102, 241, 0.1) 0%, 
        rgba(139, 92, 246, 0.1) 40%, 
        rgba(31, 41, 55, 0.7) 100%
      )
    `,
    opacity: '0',
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  const galleryImageOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryImageInfoStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '30px 25px',
    zIndex: '2',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const galleryImageInfoHoverStyles = {
    transform: 'translateY(0)',
    opacity: '1'
  };

  const galleryImageTitleStyles = {
    fontSize: '22px',
    fontWeight: '400',
    color: '#ffffff',
    marginBottom: '8px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const galleryImageTypeStyles = {
    fontSize: '12px',
    color: '#a78bfa',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  // Tablet Styles
  const galleryTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleTabletStyles = {
    fontSize: '48px'
  };

  const gallerySubtitleTabletStyles = {
    fontSize: '18px'
  };

  const galleryGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const galleryImageItemTabletStyles = {
    height: '380px'
  };

  // Mobile Styles
  const galleryMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleMobileStyles = {
    fontSize: '40px'
  };

  const gallerySubtitleMobileStyles = {
    fontSize: '16px'
  };

  const galleryGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const galleryImageItemMobileStyles = {
    height: '320px'
  };

  const galleryImageInfoMobileStyles = {
    padding: '25px 20px'
  };

  const galleryImageTitleMobileStyles = {
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryPatternOverlayClass, galleryPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageItemClass, galleryImageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageOverlayClass, galleryImageOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageInfoClass, galleryImageInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTitleClass, galleryImageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTypeClass, galleryImageTypeStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageItemClass}--hover`, galleryImageItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageClass}--hover`, galleryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageOverlayClass}--hover`, galleryImageOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageInfoClass}--hover`, galleryImageInfoHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(gallerySectionClass, galleryTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTitleClass, galleryTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(gallerySubtitleClass, gallerySubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryGridClass, galleryGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryImageItemClass, galleryImageItemTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(gallerySectionClass, galleryMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, galleryTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(gallerySubtitleClass, gallerySubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, galleryGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageItemClass, galleryImageItemMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageInfoClass, galleryImageInfoMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageTitleClass, galleryImageTitleMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryPatternOverlayClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryGridClass,
    galleryImageItemClass,
    galleryImageClass,
    galleryImageOverlayClass,
    galleryImageInfoClass,
    galleryImageTitleClass,
    galleryImageTypeClass
  ]));

  // Portrait photography images data
  const portraitImages = [
    {
      src: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=400&fit=crop&crop=center',
      title: 'Executive Professional',
      type: 'Business Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1597113117918-eb40fe0b84e7?w=600&h=400&fit=crop&crop=center',
      title: 'Natural Beauty',
      type: 'Lifestyle Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=600&h=400&fit=crop&crop=center',
      title: 'Creative Expression',
      type: 'Artistic Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600&h=400&fit=crop&crop=center',
      title: 'Corporate Leader',
      type: 'Executive Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600&h=400&fit=crop&crop=center',
      title: 'Elegant Grace',
      type: 'Fashion Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=400&fit=crop&crop=center',
      title: 'Character Study',
      type: 'Fine Art Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&h=400&fit=crop&crop=center',
      title: 'Professional Confidence',
      type: 'Business Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600&h=400&fit=crop&crop=center',
      title: 'Authentic Moment',
      type: 'Lifestyle Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=600&h=400&fit=crop&crop=center',
      title: 'Modern Professional',
      type: 'Corporate Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&h=400&fit=crop&crop=center',
      title: 'Timeless Beauty',
      type: 'Classic Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=600&h=400&fit=crop&crop=center',
      title: 'Dynamic Energy',
      type: 'Creative Portrait'
    },
    {
      src: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=400&fit=crop&crop=center',
      title: 'Sophisticated Style',
      type: 'Fashion Portrait'
    }
  ];

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Create image elements
  let currentId = gridId + 1;
  const imageElements = [];
  const imageIds = [];

  portraitImages.forEach((image, index) => {
    const itemId = currentId++;
    const imageId = currentId++;
    const overlayId = currentId++;
    const infoId = currentId++;
    const titleId = currentId++;
    const typeId = currentId++;

    imageIds.push(itemId);

    imageElements.push(
      // Image Item Container
      {
        id: itemId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Portrait Image Item ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageItemClass,
        styles: {},
        children: [imageId, overlayId, infoId],
        text: '',
        attributes: {
          'data-element': 'portrait-gallery-image-item',
          'data-index': index.toString()
        }
      },
      // Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Portrait Image ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('image'),
        className: galleryImageClass,
        styles: {},
        src: image.src,
        attributes: {
          'data-element': 'portrait-gallery-image',
          'alt': image.title
        }
      },
      // Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Portrait Image Overlay ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'portrait-gallery-image-overlay'
        }
      },
      // Info
      {
        id: infoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Portrait Image Info ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageInfoClass,
        styles: {},
        children: [titleId, typeId],
        text: '',
        attributes: {
          'data-element': 'portrait-gallery-image-info'
        }
      },
      // Title
      {
        id: titleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: `Portrait Image Title ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('h4'),
        className: galleryImageTitleClass,
        styles: {},
        children: [],
        text: image.title,
        attributes: {
          'data-element': 'portrait-gallery-image-title'
        }
      },
      // Type
      {
        id: typeId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Portrait Image Type ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('span'),
        className: galleryImageTypeClass,
        styles: {},
        children: [],
        text: image.type,
        attributes: {
          'data-element': 'portrait-gallery-image-type'
        }
      }
    );
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Portrait Sessions Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'portrait-gallery-section',
        'data-component': 'section',
        'data-section-type': 'portrait-gallery',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Portrait Gallery Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'portrait-gallery-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Portrait Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'portrait-gallery-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Portrait Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'portrait-gallery-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Portrait Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Portrait Sessions',
      attributes: {
        'data-element': 'portrait-gallery-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Portrait Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Professional portrait photography that captures personality, character, and authentic moments. From corporate headshots to creative artistic expressions.',
      attributes: {
        'data-element': 'portrait-gallery-subtitle'
      }
    },
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Portrait Gallery Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryGridClass,
      styles: {},
      children: imageIds,
      text: '',
      attributes: {
        'data-element': 'portrait-gallery-grid'
      }
    },
    ...imageElements
  ];

  return {
    portraitGallerySection: newElements[0],
    elements: newElements
  };
}; 